import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Gauge, Snowflake, Star } from 'lucide-react';
import { toast } from 'sonner';
import { Slider } from '../components/ui/slider';
import { Textarea } from '../components/ui/textarea';
import { useExperienceTheme } from '../context/ExperienceThemeContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const riskEmojis = ['😌', '🙂', '😐', '😕', '😟', '😰', '😨', '😱', '🥶', '😶', '🧊'];

const freezeTags = [
  'Too many eyes',
  'Put on the spot',
  'Unexpected question',
  'Too loud',
  "Didn't feel ready",
  'New situation',
  'Time pressure',
  'Being watched',
];

const riskTags = [
  'Classroom',
  'Friends',
  'Home',
  'Online',
  'Crowd',
  'Phone call',
  'Meeting',
  'Presentation',
];

const MissionUpdate = () => {
  const navigate = useNavigate();
  const { currentTheme, experienceTheme } = useExperienceTheme();
  const [activeTab, setActiveTab] = useState('risk'); // 'risk', 'freeze', 'checkin'
  
  // Theme terminology
  const t = currentTheme?.terminology || {};
  const colors = currentTheme?.colors || { primary: '#00f3ff', secondary: '#bc13fe' };
  
  // Risk Meter state
  const [riskValue, setRiskValue] = useState([5]);
  const [riskSelectedTags, setRiskSelectedTags] = useState([]);
  const [riskNote, setRiskNote] = useState('');
  
  // Freeze Log state
  const [freezeSelectedTags, setFreezeSelectedTags] = useState([]);
  const [freezeNote, setFreezeNote] = useState('');
  
  // Daily check-in state
  const [todayRating, setTodayRating] = useState(null);
  
  const [loading, setLoading] = useState({ risk: false, freeze: false, rating: false });

  useEffect(() => {
    fetchTodaySession();
  }, []);

  const fetchTodaySession = async () => {
    try {
      const response = await fetch(`${API}/session/today`, {
        credentials: 'include',
      });
      if (response.ok) {
        const data = await response.json();
        setTodayRating(data.mission_rating);
      }
    } catch (error) {
      console.error('Failed to fetch session:', error);
    }
  };

  const handleRiskLog = async () => {
    setLoading((prev) => ({ ...prev, risk: true }));
    try {
      const response = await fetch(`${API}/worry-log`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          value: riskValue[0],
          tags: riskSelectedTags,
          note: riskNote || null,
        }),
      });

      if (response.ok) {
        toast.success(`${t.riskMeter || 'Mission risk'} logged. Steady work, ${t.userRole || 'Pilot'}.`);
        setRiskSelectedTags([]);
        setRiskNote('');
        setRiskValue([5]);
      }
    } catch (error) {
      console.error('Failed to log risk:', error);
      toast.error(`Failed to log. Try again, ${t.userRole || 'Pilot'}.`);
    } finally {
      setLoading((prev) => ({ ...prev, risk: false }));
    }
  };

  const handleFreezeLog = async () => {
    if (freezeSelectedTags.length === 0) {
      toast.error('Select at least one tag, Pilot.');
      return;
    }

    setLoading((prev) => ({ ...prev, freeze: true }));
    try {
      const response = await fetch(`${API}/freeze-log`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          tags: freezeSelectedTags,
          note: freezeNote || null,
        }),
      });

      if (response.ok) {
        toast.success(`Data logged. Returning to safe-mode. Great work, ${t.userRole || 'Pilot'}.`);
        setFreezeSelectedTags([]);
        setFreezeNote('');
      }
    } catch (error) {
      console.error('Failed to log freeze:', error);
      toast.error(`Failed to log. Try again, ${t.userRole || 'Pilot'}.`);
    } finally {
      setLoading((prev) => ({ ...prev, freeze: false }));
    }
  };

  const handleRating = async (rating) => {
    setLoading((prev) => ({ ...prev, rating: true }));
    try {
      const response = await fetch(`${API}/session/rating`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ rating }),
      });

      if (response.ok) {
        toast.success(`Check-in logged. Good work today, ${t.userRole || 'Pilot'}.`);
        setTodayRating(rating);
      }
    } catch (error) {
      console.error('Failed to set rating:', error);
    } finally {
      setLoading((prev) => ({ ...prev, rating: false }));
    }
  };

  const toggleRiskTag = (tag) => {
    setRiskSelectedTags((prev) =>
      prev.includes(tag) ? prev.filter((t) => t !== tag) : [...prev, tag]
    );
  };

  const toggleFreezeTag = (tag) => {
    setFreezeSelectedTags((prev) =>
      prev.includes(tag) ? prev.filter((t) => t !== tag) : [...prev, tag]
    );
  };

  const tabs = [
    { id: 'risk', label: t.riskMeter || 'Mission Risk', icon: Gauge },
    { id: 'freeze', label: t.freezeLog || 'Freeze Log', icon: Snowflake },
    { id: 'checkin', label: t.dailyCheckin || 'Daily Check-in', icon: Star },
  ];

  return (
    <div className="min-h-screen theme-bg">
      {/* Background */}
      <div className="fixed inset-0 theme-gradient opacity-50 pointer-events-none" />

      {/* Header */}
      <header className="relative z-20 px-4 py-4 md:px-8 md:py-6">
        <div className="max-w-2xl mx-auto flex items-center gap-4">
          <button
            data-testid="back-btn"
            onClick={() => navigate('/dashboard')}
            className="w-10 h-10 rounded-full bg-white/5 border border-white/10 flex items-center justify-center theme-text-muted hover:theme-text hover:bg-white/10 transition-all"
          >
            <ArrowLeft size={18} />
          </button>
          <div>
            <h1 className="theme-text text-lg md:text-xl font-semibold" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
              {t.missionUpdate || 'Mission Update'}
            </h1>
            <p className="theme-text-muted text-sm">
              {t.missionUpdateDesc || 'Log your current status'}
            </p>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="relative z-10 px-4 md:px-8 pb-12">
        <div className="max-w-2xl mx-auto space-y-6">
          {/* Tab Navigation */}
          <div className="flex gap-2 overflow-x-auto pb-2 animate-fade-in-up">
            {tabs.map((tab) => (
              <button
                key={tab.id}
                data-testid={`tab-${tab.id}`}
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap transition-all ${
                  activeTab === tab.id
                    ? 'bg-white/10 theme-text border border-white/20'
                    : 'theme-text-muted hover:theme-text-secondary border border-transparent'
                }`}
              >
                <tab.icon size={16} />
                {tab.label}
              </button>
            ))}
          </div>

          {/* Mission Risk Meter */}
          {activeTab === 'risk' && (
            <div className="card-modern p-6 space-y-6 animate-fade-in-up">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full flex items-center justify-center" style={{ backgroundColor: `${colors.primary}33` }}>
                  <Gauge className="w-5 h-5" style={{ color: colors.primary }} />
                </div>
                <div>
                  <h3 className="theme-text font-semibold">{t.riskMeter || 'Mission Risk Meter'}</h3>
                  <p className="theme-text-muted text-sm">{t.riskMeterDesc || 'How risky does it feel right now?'}</p>
                </div>
              </div>

              {/* Emoji Display */}
              <div className="text-center py-6">
                <span className="text-6xl">{riskEmojis[riskValue[0]]}</span>
                <p className="text-gradient text-2xl font-bold mt-3">{riskValue[0]}/10</p>
              </div>

              {/* Slider */}
              <Slider
                data-testid="risk-slider"
                value={riskValue}
                onValueChange={setRiskValue}
                max={10}
                step={1}
                className="py-4"
              />

              {/* Tags */}
              <div>
                <p className="theme-text-muted text-sm mb-3">Context tags (optional):</p>
                <div className="flex flex-wrap gap-2">
                  {riskTags.map((tag) => (
                    <button
                      key={tag}
                      data-testid={`risk-tag-${tag.toLowerCase().replace(/\s/g, '-')}`}
                      onClick={() => toggleRiskTag(tag)}
                      className={`tag-modern ${riskSelectedTags.includes(tag) ? 'active' : ''}`}
                    >
                      {tag}
                    </button>
                  ))}
                </div>
              </div>

              {/* Note */}
              <Textarea
                data-testid="risk-note-input"
                value={riskNote}
                onChange={(e) => setRiskNote(e.target.value)}
                placeholder="Add a note (optional)..."
                className="input-modern w-full min-h-[80px] resize-none"
              />

              {/* Submit */}
              <button
                data-testid="log-risk-btn"
                onClick={handleRiskLog}
                disabled={loading.risk}
                className="btn-primary w-full py-3 disabled:opacity-50"
              >
                {loading.risk ? 'Logging...' : `Log ${t.riskMeter || 'Mission Risk'}`}
              </button>
            </div>
          )}

          {/* Freeze Log */}
          {activeTab === 'freeze' && (
            <div className="card-modern p-6 space-y-6 animate-fade-in-up">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full flex items-center justify-center" style={{ backgroundColor: `${colors.secondary}33` }}>
                  <Snowflake className="w-5 h-5" style={{ color: colors.secondary }} />
                </div>
                <div>
                  <h3 className="theme-text font-semibold">{t.freezeLog || 'Freeze Log'}</h3>
                  <p className="theme-text-muted text-sm">{t.freezeLogDesc || 'What triggered the freeze?'}</p>
                </div>
              </div>

              {/* Tags Grid */}
              <div className="grid grid-cols-2 gap-2">
                {freezeTags.map((tag) => (
                  <button
                    key={tag}
                    data-testid={`freeze-tag-${tag.toLowerCase().replace(/\s/g, '-')}`}
                    onClick={() => toggleFreezeTag(tag)}
                    className={`tag-modern tag-modern-purple text-left py-3 ${
                      freezeSelectedTags.includes(tag) ? 'active' : ''
                    }`}
                  >
                    {tag}
                  </button>
                ))}
              </div>

              {/* Note */}
              <Textarea
                data-testid="freeze-note-input"
                value={freezeNote}
                onChange={(e) => setFreezeNote(e.target.value)}
                placeholder="Add a note (optional)..."
                className="input-modern w-full min-h-[80px] resize-none"
              />

              {/* Submit */}
              <button
                data-testid="log-freeze-btn"
                onClick={handleFreezeLog}
                disabled={loading.freeze || freezeSelectedTags.length === 0}
                className="btn-secondary w-full py-3 disabled:opacity-50"
                style={{ borderColor: `${colors.secondary}4d` }}
              >
                {loading.freeze ? 'Logging...' : `Log ${t.freezeLog || 'Freeze Event'}`}
              </button>
            </div>
          )}

          {/* Daily Check-in */}
          {activeTab === 'checkin' && (
            <div className="card-modern p-6 space-y-6 animate-fade-in-up">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-yellow-500/20 flex items-center justify-center">
                  <Star className="w-5 h-5 text-yellow-400" />
                </div>
                <div>
                  <h3 className="theme-text font-semibold">{t.dailyCheckin || 'Daily Check-in'}</h3>
                  <p className="theme-text-muted text-sm">{t.dailyCheckinDesc || 'How did today feel overall?'}</p>
                </div>
              </div>

              <div className="py-8">
                <div className="flex justify-center gap-3">
                  {[1, 2, 3, 4, 5].map((rating) => (
                    <button
                      key={rating}
                      data-testid={`rating-${rating}`}
                      onClick={() => handleRating(rating)}
                      disabled={loading.rating}
                      className={`w-14 h-14 rounded-full flex items-center justify-center text-lg font-semibold transition-all ${
                        todayRating === rating
                          ? 'gradient-primary text-black glow-soft'
                          : 'bg-white/5 border border-white/10 theme-text-muted hover:border-white/30 hover:theme-text'
                      }`}
                    >
                      {rating}
                    </button>
                  ))}
                </div>
              </div>

              <p className="text-center theme-text-muted text-sm">
                {todayRating
                  ? `You rated today ${todayRating}/5. Good work logging this, ${t.userRole || 'Pilot'}.`
                  : `Rate your day when ready, ${t.userRole || 'Pilot'}.`}
              </p>
            </div>
          )}

          {/* Encouragement */}
          <div className="text-center py-6 animate-fade-in-up" style={{ animationDelay: '0.2s' }}>
            <p className="theme-text-muted text-sm">
              {t.statsEncouragement || `Every log is a step forward. Keep going, ${t.userRole || 'Pilot'}.`}
            </p>
          </div>
        </div>
      </main>
    </div>
  );
};

export default MissionUpdate;
